import {
	PAGES,
	DICTIONARIES_IN_API,
	DICTIONARY_KEYNAME,
} from "@/common/consts";
import { readLocalStorage, writeLocalStorage } from "./localStorageUtils";

export const getDefaultDictionariesByPage = (routeName = "") => {
	if (typeof routeName !== "string") routeName = "";

	switch (routeName) {
		case PAGES.UNSCRAMBLE:
		case PAGES.WWF:
		case PAGES.WORDS_CONSONANTS:
		case PAGES.WORDS_VOWELS:
		case PAGES.WORDS_ENDING_ING:
		case PAGES.WORDS_WITH_Q_NO_U:
		case PAGES.WORDS_BY_LENGTH_RESULTS:
		case PAGES.WORDS_ENDING_RESULTS:
		case PAGES.WORDS_HAVING_RESULTS:
		case PAGES.WORDS_STARTING_RESULTS:
			return DICTIONARIES_IN_API.WWF;
		case PAGES.HOME:
		case PAGES.WORD_DESCRAMBLER:
		case PAGES.SCRABBLE_DICT:
		case PAGES.SCRABBLE_DICT_RESULTS:
		case PAGES.SCRABBLE_EXACTMATCH_RESULTS:
			return DICTIONARIES_IN_API.US;
		case PAGES.WORDFEUD:
			return DICTIONARIES_IN_API.FEU;
		case PAGES.WORD_CONNECT:
			return DICTIONARIES_IN_API.CONNECT;
		case PAGES.WORDBRAIN_ANSWERS:
			return DICTIONARIES_IN_API.BRAIN;
		case PAGES.WORD_STORY:
			return DICTIONARIES_IN_API.STORY;
		case PAGES.WORD_CHUMS_CHEAT:
			return DICTIONARIES_IN_API.CHU;
		case PAGES.UNSCRAMBLER_RESULTS:
			return DICTIONARIES_IN_API.WWF;
		case PAGES.ANAGRAM:
		case PAGES.ANAGRAM_RESULTS:
			return DICTIONARIES_IN_API.ANAGRAM;
		case PAGES.FOURPICS:
			return DICTIONARIES_IN_API.PICS;
		case PAGES.JUMBLE_SOLVER:
			return DICTIONARIES_IN_API.PICS;
		case PAGES.WORDCOOKIES:
			return DICTIONARIES_IN_API.COOKIES;
		case PAGES.TEXT_TWIST:
			return DICTIONARIES_IN_API.TWIST;
		case PAGES.CODY_CROSS:
			return DICTIONARIES_IN_API.CROSS;
		case PAGES.WORD_SWIPE_CHEATS:
			return DICTIONARIES_IN_API.SWIPE;
		case PAGES.WORDLE:
		case PAGES.WORDLE_RESULTS:
			return DICTIONARIES_IN_API.WORDLE;
		default:
			return DICTIONARIES_IN_API.ALL;
	}
};

/**
 * Returns the position on the Y coord for all the values given, based on game icons sprite meta data given
 *
 * @param {Array<String>} dictionaryList List of dictionaries in a stringy fashion
 * @param {{ offsetY: Number, iconHeight: Number, position: { [key: String], Number } }} spriteMeta An object representing game icons sprite meta data for an individual tile in a vertical sprite, with an object representing positions for each of the dictionary values
 * @return {{ [givenValue: String]: Number }} Position of each of the tiles in the sprite.
 */
export const getSpriteBackgroundYPositions = (dictionaryList, spriteMeta) =>
	dictionaryList.reduce((acc, curr) => {
		const id = getDictionaryApiInternalKeyFromValue(curr) || curr;
		const multiplier = spriteMeta.position[id] || 1;
		const backgroundPosition = spriteMeta.iconHeight * multiplier;
		const backgroundPositionCorrection =
			spriteMeta.offsetY - spriteMeta.iconHeight; // remove the equivalent of the height to frame it correctly
		const position = backgroundPosition + backgroundPositionCorrection;
		acc[curr] = position === 0 ? position : position * -1; // negates to make the Y axis go negative

		return acc;
	}, {});

/**
 * Retrieves the corresponding key in DICTIONARIES_IN_API for a given value
 * This tries to use our internal keys defined in DICTIONARIES_IN_API. This way,
 * in case an endpoint changes the dictionary value parameter we need to send,
 * we wont need to change anything on our template, style classes, or dictionary keys.
 *
 * @param {String} value Value corresponding the key we want to retrieve
 * @returns {String} Key for the value specified
 */
const getDictionaryApiInternalKeyFromValue = (value) => {
	return Object.keys(DICTIONARIES_IN_API).find(
		(key) => DICTIONARIES_IN_API[key] === value
	);
};

/**
 * Builds a meta info object for dictionaries from an array of keys
 *
 * @param {Array<String>} dictionaryList An array of strings. Ideally you would want to pass a list of dictionaries specified in DICTIONARY_LISTS const.
 * @returns {Array<Object>} Array of objects representing meta info for dictionaries, used in templates and the list of dictionaries in the dictionary selectors. An empty array will be return if the list passed down is empty
 */
export const getDictionaries = (dictionaryList = []) =>
	dictionaryList.map((value) => {
		const id = getDictionaryApiInternalKeyFromValue(value) || value;
		return {
			id,
			text: `DictionaryMenuSection_${id}_Text`,
			value,
			titleAttribute: `DictionaryMenuSection_${id}_TitleAttribute`,
			ariaLabel: `DictionaryMenuSection_${id}_AriaLabel`,
		};
	});

/**
 * Retrieves a specific dictionary meta info object for a given value in a given list
 *
 * @param {Array<String>} dictionaryList An array of strings. Ideally you would want to pass a list of dictionaries specified in DICTIONARY_LISTS const.
 * @param {String} val The value that we want to find
 * @returns {Object | undefined} Meta info object for dictionaries if the value is found in the given list. Otherwise an undefined value is returned back.
 */
export const getDictionaryByValue = (dictionaryList, val) => {
	return getDictionaries(dictionaryList).find((dict) => dict.value === val);
};

export const writeDictionary = (dictionary) => {
	writeLocalStorage(DICTIONARY_KEYNAME, dictionary);
};

export const readDictionary = (defaultValue = null) => {
	if (!navigator.cookieEnabled) return defaultValue;
	let dict = readLocalStorage(DICTIONARY_KEYNAME);

	// If the dictionary stored in Local Storage is no longer available, return the default value.
	if (
		!dict.length ||
		!Object.values(DICTIONARIES_IN_API).some((val) => val === dict[0].dict)
	) {
		return defaultValue;
	}

	return dict[0].dict;
};

export const isScrabbleDictionary = (dictionary) => {
	return (
		dictionary.value === DICTIONARIES_IN_API.US ||
		dictionary.value === DICTIONARIES_IN_API.UK
	);
};

export const isWordleDictionary = (dictionary) => {
	return dictionary.value === DICTIONARIES_IN_API.WORDLE;
};
